import {
  CapabilityPage,
  type CapabilitySection,
} from "@/components/marketing/CapabilityPage";

/**
 * ``/features/faktury-a-dane`` — the documents and the tax regimes they
 * carry, and the one page of this section that says where the product
 * stops.
 *
 * ``hasBoundary`` is the „this is not an ERP" block (owner's decision
 * 2026-09-06): bookkeeping, payroll and assets stay in the shop's own
 * accounting software. It renders on the ink block, so this page is the
 * one that does NOT darken a content section — two dark blocks on one
 * page spend the contrast the boundary needs.
 *
 * Only two items are gated (``pdf_templates.editable`` from Pro,
 * ``white_label_invoices`` on Enterprise). Number series, ARES lookup,
 * VAT per country, reverse charge, OSS/IOSS, VIES and the ISDOC export
 * are on every tier and name no key.
 *
 * Both multi-item sections lead with a ``wide`` card, and the single
 * ISDOC item is ``wide`` for a different reason: a lone card in a
 * two-column grid leaves the grid's 1px gap showing as a stray block.
 */
const SECTIONS: readonly CapabilitySection[] = [
  {
    id: "documents",
    items: [
      { id: "series", wide: true },
      { id: "kinds" },
      { id: "ares" },
      { id: "templates", entitlement: "pdf_templates.editable" },
      { id: "whiteLabel", entitlement: "white_label_invoices" },
    ],
    shot: "invoicing-documents",
  },
  {
    id: "tax",
    items: [
      { id: "vat", wide: true },
      { id: "reverseCharge" },
      { id: "ossIoss" },
      { id: "vies" },
      { id: "currency" },
    ],
    shot: "invoicing-tax",
  },
  { id: "handover", items: [{ id: "isdoc", wide: true }], shot: "invoicing-handover" },
];

const PAINS = ["manual", "abroad", "accountant"] as const;
const GAINS = ["auto", "abroad", "handover"] as const;

export default function FeaturesInvoicing(): JSX.Element {
  return (
    <CapabilityPage
      i18nKey="features.pages.invoicing"
      path="/features/faktury-a-dane"
      pains={PAINS}
      gains={GAINS}
      sections={SECTIONS}
      hasBoundary
    />
  );
}
